/**
 * Availability check for fake cart lines.
 *
 * The fake cart is the only cart during browsing — nothing reaches Shopify until
 * checkout — so it never hears about catalog changes. A product drafted (or a
 * variant deleted / sold out) after a line was added keeps rendering from
 * localStorage at its frozen add-time price, and then 422s the whole atomic
 * /cart/add.js batch at checkout.
 *
 * This module gives the fake cart the missing channel: it asks the storefront
 * catalog directly, per distinct product handle, via /products/{handle}.js.
 *
 *   404                          → draft or deleted   → line removed
 *   200, product tags include OOS → soft OOS (live store) → line removed
 *   200, variant id not in list  → variant deleted    → line removed
 *   200, variant.available false → sold out           → line removed
 *   network error / non-404      → unknown            → line left alone (fail open)
 *
 * OOS matches the Liquid storefront convention (product.tags contains 'OOS'):
 * product stays published for wholesale connectors, but is treated as unavailable
 * on this live storefront.
 *
 * Removals are silent to the customer by design — the line disappears from the
 * drawer and the reason goes to the console. Fail-open is equally deliberate: a
 * flaky request must never destroy a cart.
 */
(function () {
  if (!window.FakeCart || window.FakeCartAvailability) return;

  /** Skip repeat checks within this window unless force:true (checkout always forces). */
  const THROTTLE_MS = 30000;
  const OOS_TAG = "oos";

  let lastRunAt = 0;
  let inFlight = null;
  let lastResult = { checked: false, removed: [] };

  function storeRoot() {
    const root = window.Shopify?.routes?.root || "/";
    return root.endsWith("/") ? root : `${root}/`;
  }

  function emptyVerdict(status) {
    return { status, variants: new Map(), tags: [] };
  }

  /** Normalize Ajax Product API tags (array or comma-separated string) to lowercase. */
  function normalizeTags(tags) {
    if (Array.isArray(tags)) {
      return tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
    }
    if (typeof tags === "string") {
      return tags
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);
    }
    return [];
  }

  function hasOosTag(tags) {
    return normalizeTags(tags).includes(OOS_TAG);
  }

  /**
   * Fetch one product's catalog record.
   * @returns {Promise<{status: "ok"|"gone"|"unknown", variants: Map<string, object>, tags: string[]}>}
   */
  async function fetchProduct(handle) {
    const url = `${storeRoot()}products/${encodeURIComponent(handle)}.js`;

    let response;
    try {
      response = await fetch(url, {
        credentials: "same-origin",
        // Never a cached copy: this call exists precisely to detect a catalog
        // change, and a stale 200 (or stale 404) is worse than no check at all.
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
    } catch {
      // Offline, blocked, aborted — we learned nothing.
      return emptyVerdict("unknown");
    }

    // Only 404 proves absence. A 5xx/429 is the storefront having a bad time.
    if (response.status === 404) return emptyVerdict("gone");
    if (!response.ok) return emptyVerdict("unknown");

    let product;
    try {
      product = await response.json();
    } catch {
      return emptyVerdict("unknown");
    }

    if (!product || !Array.isArray(product.variants)) {
      return emptyVerdict("unknown");
    }

    const variants = new Map();
    product.variants.forEach((variant) => {
      if (variant && variant.id != null) variants.set(String(variant.id), variant);
    });

    return { status: "ok", variants, tags: normalizeTags(product.tags) };
  }

  function describeLine(item) {
    const title = item.title || "This item";
    if (item.variant_title && item.variant_title !== "Default Title") {
      return `${title} (${item.variant_title})`;
    }
    return title;
  }

  /**
   * Why a line cannot be bought, or null if it can (or if we could not tell).
   * @returns {string|null}
   */
  function reasonUnbuyable(item, verdict) {
    if (!verdict || verdict.status === "unknown") return null;
    if (verdict.status === "gone") return "product drafted or deleted";

    // Soft OOS: still published (200) so wholesale connectors can sell it, but
    // treated as unavailable on this live storefront — same as Liquid.
    if (hasOosTag(verdict.tags)) return "OOS tag";

    const variant = verdict.variants.get(String(item.id));
    if (!variant) return "variant deleted";
    if (variant.available === false) return "sold out";

    return null;
  }

  async function runValidation() {
    const items = window.FakeCart.getItems();
    if (items.length === 0) {
      lastResult = { checked: true, removed: [] };
      return lastResult;
    }

    // One request per distinct product, not per line.
    const handles = Array.from(
      new Set(items.map((item) => item.handle).filter((handle) => Boolean(handle))),
    );

    if (handles.length === 0) {
      // Nothing addressable (metadata scrape missed the url). Checkout recovery
      // is the backstop for these.
      lastResult = { checked: false, removed: [] };
      return lastResult;
    }

    const verdicts = new Map();
    const fetched = await Promise.all(handles.map((handle) => fetchProduct(handle)));
    handles.forEach((handle, index) => verdicts.set(handle, fetched[index]));

    const removed = [];
    items.forEach((item) => {
      const reason = reasonUnbuyable(item, item.handle ? verdicts.get(item.handle) : null);
      if (reason) removed.push({ key: item.key, label: describeLine(item), reason });
    });

    if (removed.length > 0) {
      window.FakeCart.removeLines(removed.map((entry) => entry.key));
      console.warn(
        "[fake-cart] removed unavailable lines:",
        removed.map((entry) => `${entry.label} — ${entry.reason}`).join("; "),
      );
    }

    lastResult = { checked: true, removed };
    return lastResult;
  }

  window.FakeCartAvailability = {
    /**
     * Check every line against the storefront catalog and drop what cannot be
     * bought. Concurrent calls share one run; repeat calls inside THROTTLE_MS are
     * skipped unless forced.
     *
     * @param {{force?: boolean}} [options]
     * @returns {Promise<{checked: boolean, removed: object[]}>}
     */
    async validate(options = {}) {
      const force = options.force === true;

      if (inFlight) return inFlight;
      if (!force && lastRunAt && Date.now() - lastRunAt < THROTTLE_MS) {
        return { ...lastResult, checked: false };
      }

      lastRunAt = Date.now();
      inFlight = runValidation()
        .catch((error) => {
          console.warn("[fake-cart] availability check failed", error);
          return { checked: false, removed: [] };
        })
        .finally(() => {
          inFlight = null;
        });

      return inFlight;
    },

    /** @returns {object} Outcome of the most recent completed check. */
    getLastResult() {
      return { ...lastResult };
    },

    /**
     * Drop in-memory state. Called on bfcache restore: the page resumes with the
     * module variables it was frozen with, so a request that was in flight at
     * freeze time leaves `inFlight` holding a promise that may never settle —
     * which would make every later validate() hang, including the one checkout
     * awaits. The throttle stamp goes too, so a restored page re-checks instead
     * of trusting a result from before the customer left.
     */
    reset() {
      inFlight = null;
      lastRunAt = 0;
      lastResult = { checked: false, removed: [] };
    },
  };
})();
